import React, {useCallback, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';
import {useDefaultEmailValue} from '../../../../pages/Drops/Overview/useDefaultEmailValue';
import {useDropsForm} from '../../../../pages/Drops/Overview/useDropsForm';
import {useSubscribeToDrop} from '../../../../pages/Drops/Overview/useSubscribeToDrop';
import {useUpdateDropsEmailSubscriptions} from '../../../../pages/Drops/Overview/useUpdateDropsEmailSubscriptions';
import {useDropsSubscriptionsContext} from '../../../../pages/Drops/context/SubscriptionsProvider';
import {useDropsTranslationContext} from '../../../../pages/Drops/context/TranslationProvider';
import {SimpleButton} from '../../../../ui/Buttons/SimpleButton';
import {emailRegex} from '../../../../utils/email';
import {useMedia} from '../../../../utils/hooks/useMedia';
import BottomSheet from '../../../BottomSheet/BottomSheet';
import TextInput from '../../../Forms/Inputs/TextInput';
import Icon, {IconSize} from '../../../Icon/Icon';
import {GenericIconName} from '../../../Icon/IconName';
import {ToastTypes} from '../../../StatusToast/StatusToast';
import Toggle from '../../../Toggle/Toggle';
import {bordersColor} from '../../../constants/colors';
import {mediaMinTablet} from '../../../constants/customMedia';
import {fsS, fwMediumBold} from '../../../constants/typography';
import {useUserSessionContext} from '../../../sharedContexts/UserSessionContext';
import BackdropModal from '../../../utilityComponents/Modal/BackDropModal';
import {DropToast} from '../DropToast/DropToast';
import {DropTeaserArea} from '../shared/DropTeaserArea';

const DropSubscribeModalHeader = styled.div<{showBorder: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 69px;
  border-bottom: 1px solid ${bordersColor};

  ${({showBorder}) =>
    showBorder &&
    css`
      @media ${mediaMinTablet} {
        border-bottom: 0;
      }
    `};
`;

const DropSubscribeModalBody = styled.div`
  border-radius: 8px;

  @media ${mediaMinTablet} {
    width: 440px;
    min-height: 300px;
    border-radius: 0;
  }
`;

const DropSubscribeModalFooter = styled.div`
  display: flex;
  padding: 20px 30px;
`;

const StyledDropTeaserArea = styled(DropTeaserArea)`
  height: 227px;
`;

const StyledHeadline = styled.div`
  display: inline-flex;
  font-size: ${fsS}px;
  ${fwMediumBold};
  line-height: 1.25;
`;

const StyledMailIcon = styled(Icon)`
  margin-right: 6px;
`;

const StyledCancelButton = styled(SimpleButton)`
  border: 1px solid ${bordersColor};
  margin-right: 22px;
`;

const DropSubscriptionSettingLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 54px;
  padding: 0 30px;
  &:first-of-type,
  &:last-of-type {
    border-bottom: 1px solid ${bordersColor};
  }
`;

const DropSubscriptionSettingLabelForInput = styled(
  DropSubscriptionSettingLabel,
)`
  padding: 30px;
  height: auto;
`;

const DropSubscriptionInput = styled(TextInput)`
  flex: 1 1 auto;
  border: 1px solid ${bordersColor};
  border-radius: 4px;
`;

interface DropSubscriptionSelection {
  currentDrop: boolean;
  allDropsSubscriptionStatus: boolean;
}

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  dropId?: number;
  backgroundImageUrl: string;
  onDropSubscriptionSelectionChanged: (
    newSelection: DropSubscriptionSelection,
  ) => void;
  headline?: string;
  subline?: string;
  isCurrentDropDisabled: boolean;
}

interface ModalProps {
  isOpen: boolean;
  onCloseClick: () => void;
  header: React.ReactElement;
}
const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  isOpen,
  onCloseClick,
  children,
  header,
}) => {
  const isDesktop = useMedia(
    {
      [mediaMinTablet]: true,
    },
    false,
  );

  return isDesktop ? (
    <BackdropModal
      onCloseClick={onCloseClick}
      isOpen={isOpen}
      hideCloseButton={true}
    >
      {header}
      {children}
    </BackdropModal>
  ) : (
    <BottomSheet afterClose={onCloseClick} active={isOpen} header={header}>
      {children}
    </BottomSheet>
  );
};

export const DropsSubscribeModal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  onRequestClose,
  dropId,
  backgroundImageUrl,
  headline,
  subline,
  onDropSubscriptionSelectionChanged,
  isCurrentDropDisabled,
}) => {
  const translations = useDropsTranslationContext();
  const {email: userEmail} = useUserSessionContext();
  const {modalData} = useDropsSubscriptionsContext();
  const [showToast, setShowToast] = useState(false);
  const [isToastSubscribed, setIsToastSubscribed] = useState(false);
  const [toastStatus, setToastStatus] = useState<ToastTypes>('SUCCESS');
  const [preferredEmail, setPreferredEmail] = useState('');

  const defaultEmail = useDefaultEmailValue();

  const onSubscribe = useCallback(
    (status: ToastTypes, isAlwaysSubscribed: boolean) => {
      setToastStatus(status);
      setShowToast(true);
      setIsToastSubscribed(isAlwaysSubscribed);
      onRequestClose();
    },
    [setShowToast, setToastStatus, onRequestClose],
  );

  const {handleSubmit, register, errors, loadingState, setLoadingState} =
    useDropsForm();

  const [subscribeToDrop, subscribeToDropState] = useSubscribeToDrop(
    preferredEmail,
    dropId,
  );
  const [updateDropsEmailSubscriptions, updateDropsEmailSubscriptionsState] =
    useUpdateDropsEmailSubscriptions(preferredEmail);

  const formSubmitHandler = useCallback(() => {
    return handleSubmit(({email}: {email: string}) => {
      setLoadingState(true);

      const requestEmail = email || userEmail;

      if (!requestEmail) {
        return undefined;
      }

      if (!isCurrentDropDisabled && modalData.currentDrop && dropId) {
        subscribeToDrop(requestEmail, dropId);
      } else {
        updateDropsEmailSubscriptions(requestEmail);
      }

      setPreferredEmail(requestEmail);
    });
  }, [userEmail, dropId, modalData, isCurrentDropDisabled]);

  const isDesktop = useMedia(
    {
      [mediaMinTablet]: true,
    },
    false,
  );
  const handleCheckDropsChanged = useCallback(
    (dropLabel: DropSubscriptionSelection) => {
      onDropSubscriptionSelectionChanged({
        ...modalData,
        ...dropLabel,
      });
    },
    [modalData, onDropSubscriptionSelectionChanged],
  );

  const handleCheckCurrentDropChanged = useCallback(
    () =>
      handleCheckDropsChanged({
        currentDrop: !modalData.currentDrop,
        allDropsSubscriptionStatus: !modalData.currentDrop
          ? false
          : Boolean(modalData.allDropsSubscriptionStatus),
      }),
    [handleCheckDropsChanged, modalData],
  );

  useEffect(() => {
    if (
      subscribeToDropState.state !== 'pending' ||
      updateDropsEmailSubscriptionsState.state !== 'pending'
    ) {
      setLoadingState(false);
    }

    if (
      subscribeToDropState.state === 'error' ||
      updateDropsEmailSubscriptionsState.state === 'error'
    ) {
      onSubscribe('FAILURE', false);
    }
  }, [subscribeToDropState.state, updateDropsEmailSubscriptionsState.state]);

  useEffect(() => {
    if (subscribeToDropState.state === 'success') {
      onSubscribe('SUCCESS', true);
    }
  }, [subscribeToDropState.state]);

  useEffect(() => {
    if (updateDropsEmailSubscriptionsState.state === 'success') {
      onSubscribe('SUCCESS', false);
    }
  }, [updateDropsEmailSubscriptionsState.state]);

  return (
    <>
      <Modal
        onCloseClick={onRequestClose}
        isOpen={isOpen}
        header={
          <DropSubscribeModalHeader
            showBorder={isDesktop && backgroundImageUrl.length !== 0}
          >
            <StyledHeadline>
              <StyledMailIcon
                asset={GenericIconName.IcMail}
                size={IconSize.S}
              />
              {translations.overview.subscribe.modal.activateNotifications}
            </StyledHeadline>
          </DropSubscribeModalHeader>
        }
      >
        <form onSubmit={formSubmitHandler()}>
          <DropSubscribeModalBody>
            {isDesktop && (
              <StyledDropTeaserArea
                headline={headline}
                subline={subline}
                backgroundImageUrl={backgroundImageUrl}
              />
            )}

            <DropSubscriptionSettingLabelForInput htmlFor="subscribeEmail">
              <DropSubscriptionInput
                id="subscribeEmail"
                name="email"
                type="email"
                ref={register({
                  required:
                    translations.overview.subscribe.modal.formErrors.email
                      .empty,
                  pattern: {
                    value: emailRegex,
                    message:
                      translations.overview.subscribe.modal.formErrors.email
                        .invalid,
                  },
                })}
                label={translations.overview.subscribe.modal.email}
                errorMessage={errors.email?.message}
                value={defaultEmail}
              />
            </DropSubscriptionSettingLabelForInput>

            {dropId && (
              <DropSubscriptionSettingLabel htmlFor="checkCurrentDrop">
                {translations.overview.subscribe.modal.this}
                <Toggle
                  checked={modalData.currentDrop}
                  onChange={handleCheckCurrentDropChanged}
                  id="checkCurrentDrop"
                  disabled={isCurrentDropDisabled}
                />
              </DropSubscriptionSettingLabel>
            )}

            <DropSubscribeModalFooter>
              <StyledCancelButton
                onClick={onRequestClose}
                buttonType="secondary"
              >
                {translations.overview.subscribe.modal.cancel}
              </StyledCancelButton>
              <SimpleButton type="submit" loading={loadingState}>
                {translations.overview.subscribe.modal.save}
              </SimpleButton>
            </DropSubscribeModalFooter>
          </DropSubscribeModalBody>
        </form>
      </Modal>
      <DropToast
        status={toastStatus}
        isShown={showToast}
        isSubscribed={isToastSubscribed}
        onClose={() => setShowToast(false)}
      />
    </>
  );
};
